import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { extractBankCode, normalizeText, parseMetadata } from './cashout-bank.helpers';

export const NOTBANK_FIAT_CURRENCIES = ['COP', 'CLP', 'BRL', 'PEN', 'ARS'] as const;

export const NOTBANK_KIND_OPTIONS: Record<string, { value: string; label: string }[]> = {
  CO: [
    { value: 'corriente', label: 'Corriente' },
    { value: 'ahorro', label: 'Ahorro' },
  ],
  CL: [
    { value: 'corriente', label: 'Corriente' },
    { value: 'vista', label: 'Vista' },
    { value: 'ahorro', label: 'Ahorro' },
    { value: 'electronic_checkbook', label: 'Chequera electrónica' },
  ],
  PE: [
    { value: 'corriente', label: 'Corriente' },
    { value: 'ahorro', label: 'Ahorro' },
  ],
  BR: [
    { value: 'br_pix', label: 'PIX' },
    { value: 'br_corriente_fisica', label: 'Corriente física' },
    { value: 'br_poupanca_fisica', label: 'Poupança física' },
    { value: 'br_simple_fisica', label: 'Simple física' },
  ],
  AR: [
    { value: 'ar_cbu', label: 'CBU' },
    { value: 'ar_cvu', label: 'CVU' },
    { value: 'ar_alia', label: 'Alia' },
    { value: 'corriente', label: 'Corriente' },
    { value: 'ahorro', label: 'Ahorro' },
  ],
};

export const NOTBANK_PIX_TYPE_OPTIONS = [
  { value: 'Email', label: 'Email' },
  { value: 'CPF', label: 'CPF' },
  { value: 'Phone', label: 'Teléfono' },
  { value: 'Random', label: 'EVP / aleatoria' },
];

export function countryForNotbankCurrency(currency: string): string {
  const map: Record<string, string> = {
    COP: 'CO',
    CLP: 'CL',
    BRL: 'BR',
    PEN: 'PE',
    ARS: 'AR',
  };
  return map[(currency || '').toUpperCase()] || '';
}

/** True when selected company/provider is a Notbank LatAm rail. */
export function isNotbankBankSelected(ctx: any): boolean {
  const bank = ctx?.newTransactionForm?.controls?.company_id?.value;
  const code = (extractBankCode(bank) ?? '').toString().trim().toUpperCase();
  if (code.includes('NOTBANK')) {
    return true;
  }
  if (typeof bank === 'object' && bank) {
    const metadata = parseMetadata(bank.metadata);
    const provider = String(metadata?.['provider'] ?? '')
      .trim()
      .toLowerCase();
    if (provider === 'notbank') {
      return true;
    }
  }
  const name = String(bank?.name || bank?.company_name || '').toLowerCase();
  return normalizeText(name).includes('notbank');
}

export function createNotbankBankFormGroup(fb: FormBuilder): FormGroup {
  // No required by default — other cashout currencies share this form group.
  return fb.group({
    bank: [null as any],
    number: [''],
    kind: [''],
    pix_type: [''],
    agency: [''],
    dv: [''],
    province: [null as any],
    name_1: [''],
    name_2: [''],
    cbu: [''],
    person_type: ['F'],
    cuit: [''],
  });
}

export function clearNotbankBankValidators(group: FormGroup | null | undefined): void {
  if (!group) {
    return;
  }
  Object.keys(group.controls).forEach((name) => {
    const c = group.get(name);
    c?.clearValidators();
    c?.updateValueAndValidity({ emitEvent: false });
  });
}

export function updateNotbankBankValidators(group: FormGroup, currency: string): void {
  if (!group) {
    return;
  }
  const ccy = (currency || '').toUpperCase();
  const country = countryForNotbankCurrency(ccy);
  const kind = String(group.get('kind')?.value || '').toLowerCase();

  const setReq = (name: string, required: boolean) => {
    const c = group.get(name);
    if (!c) return;
    c.setValidators(required ? [Validators.required] : []);
    c.updateValueAndValidity({ emitEvent: false });
  };

  setReq('bank', true);
  setReq('number', true);
  setReq('kind', true);
  setReq('province', country === 'PE');
  setReq('pix_type', country === 'BR' && kind === 'br_pix');
  setReq('agency', country === 'BR' && kind !== 'br_pix' && !!kind);
  setReq('dv', country === 'BR' && kind !== 'br_pix' && !!kind);
  setReq('cbu', ccy === 'ARS');
  setReq('person_type', ccy === 'ARS');
  setReq('cuit', ccy === 'ARS');
}

export function buildNotbankBankAccountPayload(
  group: FormGroup,
  currency: string,
  ctx?: any,
): Record<string, unknown> | null {
  if (!group) {
    return null;
  }
  updateNotbankBankValidators(group, currency);
  group.markAllAsTouched();
  if (group.invalid) {
    return null;
  }
  const v = group.getRawValue() as Record<string, any>;
  const bankObj = v['bank'];
  const bankId =
    typeof bankObj === 'object' && bankObj
      ? bankObj.id || bankObj.bank_id || bankObj.uuid
      : bankObj;
  if (!bankId) {
    return null;
  }

  const first =
    (v['name_1'] || '').toString().trim() ||
    (ctx?.newTransactionForm?.controls?.firstname?.value || '').toString().trim();
  const last =
    (v['name_2'] || '').toString().trim() ||
    (ctx?.newTransactionForm?.controls?.lastname?.value || '').toString().trim();

  const payload: Record<string, unknown> = {
    bank: String(bankId),
    number: String(v['number'] || '').trim(),
    kind: String(v['kind'] || '').trim(),
    currency: (currency || '').toUpperCase(),
    country: countryForNotbankCurrency(currency),
  };
  if (v['pix_type']) payload['pix_type'] = String(v['pix_type']).trim();
  if (v['agency']) payload['agency'] = String(v['agency']).trim();
  if (v['dv']) payload['dv'] = String(v['dv']).trim();
  if (v['province']) {
    const prov = v['province'];
    payload['province'] =
      typeof prov === 'object' ? prov.id || prov.code || prov.name : prov;
  }
  if (first) payload['name_1'] = first;
  if (last) payload['name_2'] = last;
  return payload;
}

export function unwrapNotbankList(resp: any): any[] {
  const data = resp?.data ?? resp;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.data)) return data.data.data;
  return [];
}
